import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function ForkliftInfo({ forkliftInfo, onClose }) {
  const { isDark } = useTheme();
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamLoading, setStreamLoading] = useState(true);
  const [streamError, setStreamError] = useState(false);

  // Set up video stream from Raspberry Pi camera
  useEffect(() => {
    if (!forkliftInfo?.asset?.camera_port) {
      setStreamUrl(null);
      setStreamLoading(false);
      return;
    }

    // Get stream URL from tag metadata (auto-detected from Pi)
    // The Pi automatically sends its stream_url when sending detections
    const streamUrlFromMetadata = forkliftInfo.tag.metadata?.stream_url;
    const piIp = forkliftInfo.tag.metadata?.pi_ip || forkliftInfo.tag.pi_ip;
    const streamPath = forkliftInfo.tag.metadata?.stream_path || forkliftInfo.tag.stream_path || '/stream-full';
    const cameraPort = forkliftInfo.asset.camera_port || 8080;
    
    // Connect directly to Raspberry Pi (works when user is on same network)
    let url;
    if (streamUrlFromMetadata) {
      // Use the automatically detected stream URL from the Pi
      url = streamUrlFromMetadata;
      console.log(`📹 Using AUTO-DETECTED stream URL from Pi: ${url}`);
    } else if (piIp) {
      // Construct URL from IP if stream_url not available
      url = `http://${piIp}:${cameraPort}${streamPath}`;
      console.log(`📹 Setting up DIRECT stream from Pi IP: ${url}`);
    } else {
      // Fallback: try backend proxy if no stream info is set
      const hardwareId = forkliftInfo.tag.tag_hardware_id || forkliftInfo.tag.tag_id;
      url = `/api/vision/stream/${hardwareId}?port=${cameraPort}`;
      console.log(`📹 Setting up PROXIED stream (no stream info set): ${url}`);
    }
    
    setStreamUrl(url);
    setStreamLoading(true);
    setStreamError(false);

    return () => {
      setStreamUrl(null);
      setStreamLoading(false);
      setStreamError(false);
    };
  }, [forkliftInfo?.asset?.camera_port, forkliftInfo?.tag?.tag_hardware_id, forkliftInfo?.tag?.tag_id, forkliftInfo?.tag?.metadata, forkliftInfo?.tag?.pi_ip, forkliftInfo?.tag?.stream_path]);

  if (!forkliftInfo) return null;

  const { tag, asset, position, confidence } = forkliftInfo;

  return (
    <div 
      className={`pointer-events-auto max-w-[380px] w-full rounded-lg shadow-xl border ${
        isDark 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}
      style={{ marginTop: '16px' }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b ${
        isDark ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
            <span className="text-white text-xl">🚜</span>
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {tag.tag_name || tag.tag_id}
            </h3>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Carrying Asset
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className={`p-1 rounded hover:bg-opacity-20 ${
            isDark 
              ? 'text-gray-400 hover:bg-gray-700' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Video Stream - MJPEG stream from Raspberry Pi */}
        {streamUrl && (
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
            <img
              src={streamUrl}
              alt="Forklift camera stream"
              className="w-full h-full object-contain"
              onLoad={() => {
                console.log('📹 Stream loaded successfully');
                setStreamLoading(false);
              }}
              onError={(e) => {
                console.error('📹 Stream failed to load:', streamUrl);
                console.error('📹 Error details:', e);
                console.error('📹 Tag info:', {
                  tag_id: forkliftInfo.tag.tag_id,
                  tag_hardware_id: forkliftInfo.tag.tag_hardware_id,
                  pi_ip: forkliftInfo.tag.metadata?.pi_ip || forkliftInfo.tag.pi_ip,
                  camera_port: forkliftInfo.asset.camera_port
                });
                setStreamLoading(false);
                setStreamError(true);
              }}
              style={{ display: streamError ? 'none' : 'block' }}
            />
            {streamLoading && !streamError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm text-gray-300">Loading stream...</p>
                </div>
              </div>
            )}
            {streamError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center p-4">
                  <p className="text-sm text-red-400 mb-2">Failed to load camera stream</p>
                  <p className="text-xs text-gray-400">Check Raspberry Pi connection</p>
                  <p className="text-xs text-gray-500 mt-2">URL: {streamUrl}</p>
                </div>
              </div>
            )}
            {/* Show stream URL info */}
            {streamUrl && !streamError && (
              <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-70 rounded px-2 py-1">
                <p className="text-xs text-gray-300 truncate">
                  📹 {forkliftInfo.tag.metadata?.stream_url ? 'Auto-detected' : 'Manual'} stream: {streamUrl}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Forklift Information */}
        <div>
          <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Forklift Status
          </h4>
          <div className={`space-y-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <div className="flex justify-between">
              <span>Tag ID:</span>
              <span className="font-mono">{tag.tag_id}</span>
            </div>
            <div className="flex justify-between">
              <span>Position:</span>
              <span className="font-mono">
                ({position?.x?.toFixed(1)}, {position?.y?.toFixed(1)})
              </span>
            </div>
            <div className="flex justify-between">
              <span>Confidence:</span>
              <span>{(confidence * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span>Status:</span>
              <span className="text-green-500 font-semibold">Carrying Asset</span>
            </div>
          </div>
        </div>

        {/* Asset Information */}
        {asset && (
          <div>
            <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Asset Information
            </h4>
            <div className={`space-y-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className="flex justify-between">
                <span>Asset ID:</span>
                <span className="font-mono">{asset.code || asset.id}</span>
              </div>
              {asset.name && (
                <div className="flex justify-between">
                  <span>Name:</span>
                  <span>{asset.name}</span>
                </div>
              )}
              {asset.type && (
                <div className="flex justify-between">
                  <span>Type:</span>
                  <span>{asset.type}</span>
                </div>
              )}
              {asset.status && (
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span>{asset.status}</span>
                </div>
              )}
              {asset.zone_name && (
                <div className="flex justify-between">
                  <span>Zone:</span>
                  <span>{asset.zone_name}</span>
                </div>
              )}
              {asset.detected_at && (
                <div className="flex justify-between">
                  <span>Detected:</span>
                  <span className="text-xs">
                    {new Date(asset.detected_at).toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
